/**
 * bridgeClubSessionSource — the REAL (Plan 4) backing for the Club seam.
 *
 * Implements the SAME synchronous ClubSessionSource the Plan-3 fixtures did, but
 * sourced from live native data: the viewer's own swims, pool or open water
 * (listWorkouts + per-swim getWorkoutFirehose) plus each synced companion's ONE
 * shared swim (listCompanions + getCompanionFirehose).
 *
 * The native publisher admits BOTH pool and open-water swims
 * (SwimPublisher.isClubPublishable = pool || openWater), and this web layer now
 * admits both too: viewer swims and companion swims are no longer filtered to
 * open-water-only. Per-swim location (`swimmingLocationType`) rides along on the
 * WorkoutSummary/firehose untouched, so downstream UI can still branch OW-only
 * chrome (route map, water temp, "open water" marker) per swim.
 *
 * The seam is synchronous by contract, the bridge is async, so this is an async
 * LOADER: it pre-fetches every firehose the temp resolution could read, builds
 * lookup maps, and returns a plain sync snapshot. Each individual fetch failure
 * degrades to null WITH a console.warn — never a wholesale rejection, never a
 * silent fallback.
 */
import type { MemberSwim } from "./clubSessions";
import type { ClubSessionSource } from "./clubSessionSource";
import type { WorkoutSummary, CompanionSummary, SwimFirehoseResponse, AttendanceEntry, LapLabel } from "@contract/types";
import { bridge, warnToNative } from "../bridge/client";
import { loadSavedLabels } from "./savedLabels";
import { supportsMethod } from "./nativeMethods";

/** Sentinel member id for the viewer's own swims. CloudKit companion originIds
 *  are underscore-prefixed record hashes, so this never collides. */
export const VIEWER_ID = "viewer";

/** Own swims the loader asks native for (B-016, build 11): native clamps `limit` to
 *  1000 from build 11 on; an older shell answers its old ceiling of 100. */
export const CLUB_OWN_SWIMS_LIMIT = 1000;

/** Own swims whose firehose and saved corrections are pre-fetched: the newest 50, the
 *  same count the loader read before build 11 (a firehose read is the expensive part).
 *  An older own swim reads as "no firehose" on the day card, like a clubmate's. */
export const CLUB_OWN_FIREHOSE_LIMIT = 50;

export interface BridgeSourceDeps {
  listWorkouts: (args?: { limit?: number; activityType?: string }) => Promise<WorkoutSummary[]>;
  listCompanions: () => Promise<CompanionSummary[]>;
  getCompanionFirehose: (args: { originId: string }) => Promise<SwimFirehoseResponse | null>;
  getWorkoutFirehose: (args: { workoutUUID: string }) => Promise<SwimFirehoseResponse | null>;
  listAttendance: () => Promise<AttendanceEntry[]>;
  getLapLabels: (workoutUUID: string) => Promise<LapLabel[]>;
  /** A clubmate's published corrections for one swim (LapLabels record). An older
   *  shell without the method resolves [] so the strip shows the watch's strokes. */
  getCompanionLapLabels: (args: { originId: string; workoutUUID: string }) => Promise<LapLabel[]>;
}

const realDeps: BridgeSourceDeps = {
  listWorkouts: (args) => bridge.listWorkouts(args),
  listCompanions: () => bridge.listCompanions(),
  getCompanionFirehose: (args) => bridge.getCompanionFirehose(args),
  getWorkoutFirehose: (args) => bridge.getWorkoutFirehose(args),
  listAttendance: () => bridge.listAttendance(),
  getLapLabels: (uuid) => bridge.getLapLabels(uuid),
  getCompanionLapLabels: (args) =>
    supportsMethod("getCompanionLapLabels") ? bridge.getCompanionLapLabels(args) : Promise.resolve([]),
};

/** A failed fetch degrades to null, visibly logged — never rejects the load. */
function nullOnError<T>(p: Promise<T | null>, what: string): Promise<T | null> {
  return p.catch((e) => {
    warnToNative(`[club] ${what} fetch failed`, e);
    return null;
  });
}

export async function loadBridgeClubSessionSource(
  deps: BridgeSourceDeps = realDeps,
): Promise<ClubSessionSource> {
  // 1. Viewer's own swims, pool AND open water: every swimming workout HealthKit
  //    returns, newest first (no location filter on either side; B-009 corrected
  //    the old "native gates this" note). The whole history (B-016): the Club
  //    calendar counts own swims months back, like the Your swims calendar.
  const viewerSwims = await deps.listWorkouts({ limit: CLUB_OWN_SWIMS_LIMIT });
  const viewerDetailed = viewerSwims.slice(0, CLUB_OWN_FIREHOSE_LIMIT);

  // 2. Companions + viewer firehoses + attendance, all fetched in parallel;
  //    individual failures degrade to null/[] (omitted temp / dropped companion /
  //    no attendee rows) — attendance is enrichment, not the card's spine. An old
  //    shell without listAttendance errors BRIDGE_METHOD_UNKNOWN → warn + [].
  const companions = await deps.listCompanions();
  const [companionFhs, viewerFhEntries, attendanceRaw, viewerLabelEntries] = await Promise.all([
    Promise.all(
      companions.map((c) =>
        nullOnError(deps.getCompanionFirehose({ originId: c.originId }), `companion ${c.originId}`),
      ),
    ),
    Promise.all(
      viewerDetailed.map(async (w) =>
        [w.uuid, await nullOnError(deps.getWorkoutFirehose({ workoutUUID: w.uuid }), `swim ${w.uuid}`)] as const,
      ),
    ),
    nullOnError<AttendanceEntry[]>(deps.listAttendance(), "attendance"),
    // The viewer's saved stroke corrections, through the one loader every other
    // surface uses (a failed read warns and derives the watch's strokes).
    Promise.all(viewerDetailed.map(async (w) => [w.uuid, await loadSavedLabels(w.uuid, deps.getLapLabels)] as const)),
  ]);
  const viewerFhByUUID = new Map<string, SwimFirehoseResponse | null>(viewerFhEntries);
  const viewerLabelsByUUID = new Map<string, LapLabel[]>(viewerLabelEntries);
  // Each clubmate's published corrections for the swim we hold (stroke-sync).
  // Native returns only labels the swim's own creator wrote; a failed read warns
  // and that strip shows the watch's strokes.
  const companionLabels = await Promise.all(
    companions.map(async (c, i) => {
      const fh = companionFhs[i];
      if (!fh) return [] as LapLabel[];
      return deps.getCompanionLapLabels({ originId: c.originId, workoutUUID: fh.workout.uuid }).catch((e) => {
        warnToNative(`[club] companion ${c.originId} corrections fetch failed`, e);
        return [] as LapLabel[];
      });
    }),
  );
  const companionLabelsByKey = new Map<string, LapLabel[]>(
    companions.map((c, i) => [`${c.originId}|${companionFhs[i]?.workout.uuid ?? ""}`, companionLabels[i]]),
  );

  // Viewer-identity mapping (audit F2 — was blocking): native stores the local tap
  // under CloudKit's own-records token `__defaultOwner__` (CKCurrentUserDefaultName
  // resolves to the same string — CompanionSyncController.isLocalUser), while this
  // seam's viewer id is VIEWER_ID. Without the mapping the viewer's own tap row
  // renders isViewer=false beside swim rows labeled "You", and the button-hide check
  // in SharedMorningView never matches (the button re-shows after a successful tap).
  const attendance: AttendanceEntry[] = (attendanceRaw ?? []).map((a) =>
    a.memberId === "__defaultOwner__" ? { ...a, memberId: VIEWER_ID } : a,
  );

  // 3. Assemble swims + companion lookups.
  const swims: MemberSwim[] = viewerSwims.map((w) => ({ memberId: VIEWER_ID, swim: w }));
  const labels = new Map<string, string>();
  const companionFhByMember = new Map<string, SwimFirehoseResponse>();
  companions.forEach((c, i) => {
    labels.set(c.originId, c.swimmerLabel);
    const fh = companionFhs[i];
    if (fh) {
      swims.push({ memberId: c.originId, swim: fh.workout });
      companionFhByMember.set(c.originId, fh);
    }
  });

  return {
    swims,
    viewerId: VIEWER_ID,
    labelFor: (id) => (id === VIEWER_ID ? "You" : labels.get(id) ?? id),
    // A clubmate's firehose answers only for THAT swim: the club snapshot adds a
    // clubmate's older swims to the sessions (clubCalendar.mergeClubSwims), and
    // those must read as "no firehose", never as the latest swim's sets.
    firehoseFor: (memberId, uuid) => {
      if (memberId === VIEWER_ID) return viewerFhByUUID.get(uuid) ?? null;
      const fh = companionFhByMember.get(memberId);
      return fh && fh.workout.uuid === uuid ? fh : null;
    },
    attendance,
    labelsFor: (memberId, uuid) =>
      memberId === VIEWER_ID
        ? viewerLabelsByUUID.get(uuid) ?? []
        : companionLabelsByKey.get(`${memberId}|${uuid}`) ?? [],
  };
}
